// **The cue list** (`demo-presentation-shell` task 3.2, design **DD2**): the single typed,
// linear array the sequencer steps. Every entry corresponds, one-to-one and in order, to a beat in
// `client/src/test/beat-manifest.ts` — the independent oracle authored from `docs/PRESENTATION.md`
// before this file existed (task 1.7). `cues.test.ts` asserts that correspondence exactly; this
// file's ordering, ids and kinds are written to satisfy that test, never the reverse.
//
// A `chapter` marker rides on every cue belonging to one of the three numbered chapters, so the
// number-key jump and the stage-setup re-run (design **DD13**) are both lookups over this one
// array — "the sequencer holds one integer" (DD2). Cues outside a numbered chapter (the opening
// language slides, the Live Match and Rounds hand-off, the wrap-up) carry no chapter marker.

import type {
  DemoCommandOp,
  DemoHotSwapOp,
  DemoSpawnOp,
  DemoStageSpec,
} from "./stage-driver";
import {
  CHAPTER3_FULL_HIVE_STAGE,
  CONTINUOUS_STAGE,
  ENEMY_RAID_POSITION,
  ENEMY_RAID_TARGET,
} from "./chapters";

/** The three numbered chapters a presenter can jump to by digit key. Chapters 2 and 3 open on
 * **chapter 1's** world (see `chapters.ts`'s header) and only the full-hive beat restages, so a
 * jump to either rebuilds that one world and replays every stage cue up to the target — the raid
 * included — rather than configuring a chapter-shaped world of its own. */
export type ChapterId = "chapter1" | "chapter2" | "chapter3";

/** A slide's stable id — reuses the beat id, so there is no second naming scheme to keep in sync
 * with the beat manifest. */
export type SlideId = string;

/** The four cue-gated HUD elements (task 8's "four HUD `ui` cues", in their scripted order). */
export type HudElement =
  | "nectarCounter"
  | "beeCounter"
  | "promptBar"
  | "typeBar";

/** The three modals — `api-modal.html`'s two layouts, plus chapter 3's flowchart view of the
 * program the Beekeeper just wrote (docs/PRESENTATION.md's own technical requirement, 2026-08-09).
 * The flowchart is a modal rather than a slide because it is read *over the running world*, and
 * because the modal machinery already gives it a show cue and a dismiss cue. */
export type ModalId =
  | "sixActionsModal"
  | "fullVocabularyModal"
  | "builderFlowchartModal";

/** Every token a `ui` cue's `show` field can carry, beyond the HUD elements and modals: the three
 * chapter-title overlays, the prompt bar's scope change, and the two rounds-mode UI reveals that
 * ride alongside the tournament `nav` chapters. */
export type ChromeToken =
  | "chapter1Title"
  | "chapter2Title"
  | "chapter3Title"
  | "promptScopeBeekeeper"
  | "winnersPrompts"
  | "nextButton";

export type UiToken = HudElement | ModalId | ChromeToken;

export function isModal(token: UiToken): token is ModalId {
  return (
    token === "sixActionsModal" ||
    token === "fullVocabularyModal" ||
    token === "builderFlowchartModal"
  );
}

/** The tournament host component a `nav` cue mounts (design **DD9**). Exact naming is this
 * change's own placeholder — group 12.5 reconciles it against `tournament-big-screen`'s real
 * component names when that dependency lands. */
export type TournamentChapter = "gathering" | "leaderboard" | "playback";
/** Projector chapters for the presentation's Live Match. (`roundsHandoff` was removed 2026-08-13:
 * the hand-off is a slide now — see the cue list's note.) */
export type PresentationChapter = "setup" | "liveMatch";
export type DemoNavTarget = TournamentChapter | PresentationChapter;

export type StageOp =
  | { kind: "configure"; stage: DemoStageSpec }
  | { kind: "spawn"; spawn: DemoSpawnOp }
  | { kind: "hotSwap"; hotSwap: DemoHotSwapOp }
  | { kind: "fastForward"; ticks: number }
  | { kind: "command"; command: DemoCommandOp }
  /** A beat whose world mutation happens through live presenter/agent interaction built by another
   * lane (the typed harvester program — group 9; the Beekeeper-created builder type — group 10),
   * not through scripted content in this list. Advancing past it is inert; the cue exists so the
   * beat has a position in the list for the correspondence check (`cues.test.ts`) and the
   * rehearsal walk (task 13.4) to find. */
  | { kind: "checkpoint" };

interface CueBase {
  /** The beat this cue realises — must match a `client/src/test/beat-manifest.ts` entry's `id`
   * exactly (`cues.test.ts` asserts it). */
  readonly beatId: string;
  /** Which numbered chapter this cue belongs to, if any (see module header). */
  readonly chapter?: ChapterId;
}

export type Cue =
  | (CueBase & { readonly kind: "slide"; readonly slide: SlideId })
  | (CueBase & { readonly kind: "reveal" })
  | (CueBase & { readonly kind: "stage"; readonly op: StageOp })
  | (CueBase & { readonly kind: "ui"; readonly show: UiToken | null })
  | (CueBase & { readonly kind: "nav"; readonly target: DemoNavTarget });

export const CUE_KINDS = ["slide", "reveal", "stage", "ui", "nav"] as const;

export const CUE_LIST: readonly Cue[] = [
  // --- Opening & language slides ------------------------------------------------------------
  { beatId: "slide-cover", kind: "slide", slide: "slide-cover" },
  {
    beatId: "slide-vibe-coding-quote",
    kind: "slide",
    slide: "slide-vibe-coding-quote",
  },
  { beatId: "slide-apiophobia", kind: "slide", slide: "slide-apiophobia" },
  // **Three reveals, in the order the presenter says the sentence** (`APIOPHOBIA_MAX_REVEAL` 3):
  // the Greek suffix first, then the Latin stem, then what they add up to. `-phobia` leading is
  // deliberate and confirmed, not a transcription slip.
  { beatId: "reveal-apiophobia-phobia", kind: "reveal" },
  { beatId: "reveal-apiophobia-api", kind: "reveal" },
  { beatId: "reveal-apiophobia-origins", kind: "reveal" },
  {
    beatId: "slide-translation-vibes-chart",
    kind: "slide",
    slide: "slide-translation-vibes-chart",
  },
  // Both English sentences arrive **with** the slide, so it never looks broken while the presenter
  // sets the example up. `LOVE_MAX_REVEAL` is 3: the English/Greek split, then storgē attaching
  // itself to the family, then hēdonē for the tiramisu. Three reveal cues where there were two —
  // `HANDOFF.md` said this change *removes* a cue "since both sentences now enter together", which
  // was wrong about the shipped slide: `LoveSlide` already rendered both sentences unconditionally
  // at step 0, so no cue was ever bringing the second one in.
  {
    beatId: "slide-love-sentences",
    kind: "slide",
    slide: "slide-love-sentences",
  },
  { beatId: "reveal-greek-loves", kind: "reveal" },
  { beatId: "reveal-storge-for-family", kind: "reveal" },
  { beatId: "reveal-hedone", kind: "reveal" },
  // "The translation chart returns, now with German added" — the same component, mounted at a base
  // reveal step of 1 (`SlideDeck.tsx`), so the chart has one implementation and two entrances.
  {
    beatId: "slide-translation-chart-german",
    kind: "slide",
    slide: "slide-translation-chart-german",
  },
  { beatId: "reveal-hug", kind: "reveal" },
  {
    beatId: "slide-programming-as-translation",
    kind: "slide",
    slide: "slide-programming-as-translation",
  },
  { beatId: "reveal-language-range", kind: "reveal" },
  { beatId: "reveal-llm", kind: "reveal" },

  // --- Chapter 1: point-and-click -----------------------------------------------------------
  // **`ui-nectar-counter` is gone** (presentation-polish Wave 2, 2026-08-11): the configure now
  // establishes the counter itself (`ui-state.ts`), so the chapter title — the thing that actually
  // announces the chapter — arrives on the presenter's first click rather than their second. The cue
  // had in any case been inert for the counter's *visibility* since 2026-08-09, when `DemoApp`
  // started rendering it on `onStage` so it would survive a reversal; deleting it and establishing
  // the token on the configure replaces those two mechanisms with one.
  {
    beatId: "stage-chapter1-configure",
    kind: "stage",
    chapter: "chapter1",
    op: { kind: "configure", stage: CONTINUOUS_STAGE },
  },
  {
    beatId: "ui-chapter1-title",
    kind: "ui",
    chapter: "chapter1",
    show: "chapter1Title",
  },
  {
    beatId: "stage-chapter1-spawn-second-bee",
    kind: "stage",
    chapter: "chapter1",
    op: { kind: "spawn", spawn: { colony: 0, beeType: "harvester", count: 1 } },
  },
  {
    beatId: "stage-chapter1-spawn-eight-more",
    kind: "stage",
    chapter: "chapter1",
    op: { kind: "spawn", spawn: { colony: 0, beeType: "harvester", count: 8 } },
  },
  {
    beatId: "ui-prompt-bar-instruct",
    kind: "ui",
    chapter: "chapter1",
    show: "promptBar",
  },
  // **"A scripted enemy bee comes in"** — a spawn, not a hot-swap (E2E round-3 finding H). The
  // enemy colony already carries its raiding program; the bee now *arrives*: spawned at the
  // field's side and flown to the comb's rim under a `move_to` override (2026-08-09 request), so
  // the audience watches it come in and `raze` starts on the outermost cells.
  {
    beatId: "stage-chapter1b-enemy-activation",
    kind: "stage",
    chapter: "chapter1",
    op: {
      kind: "spawn",
      spawn: {
        colony: 1,
        beeType: "raider",
        count: 1,
        position: ENEMY_RAID_POSITION,
        moveTo: ENEMY_RAID_TARGET,
      },
    },
  },
  {
    beatId: "stage-chapter1b-back-to-work",
    kind: "stage",
    chapter: "chapter1",
    op: { kind: "command", command: { colony: 0, command: "resume" } },
  },

  // --- Chapter 2: hand-written B++ -----------------------------------------------------------
  // No configure of its own: chapter 2 is narrated over the world chapter 1 has been running,
  // damaged comb and all (`chapters.ts`'s header). The script has no staging instruction here
  // either — it opens straight on the six-action modal — so the manifest has no beat for one.
  {
    beatId: "ui-chapter2-title",
    kind: "ui",
    chapter: "chapter2",
    show: "chapter2Title",
  },
  {
    beatId: "ui-modal-six-actions-show",
    kind: "ui",
    chapter: "chapter2",
    show: "sixActionsModal",
  },
  {
    beatId: "ui-modal-six-actions-dismiss",
    kind: "ui",
    chapter: "chapter2",
    show: null,
  },
  {
    beatId: "ui-type-bar-harvester",
    kind: "ui",
    chapter: "chapter2",
    show: "typeBar",
  },
  {
    beatId: "stage-chapter2-run-harvester-program",
    kind: "stage",
    chapter: "chapter2",
    op: { kind: "checkpoint" },
  },
  {
    beatId: "ui-modal-full-vocabulary-show",
    kind: "ui",
    chapter: "chapter2",
    show: "fullVocabularyModal",
  },
  {
    beatId: "ui-modal-full-vocabulary-dismiss",
    kind: "ui",
    chapter: "chapter2",
    show: null,
  },

  // --- Chapter 3: vibe coding ----------------------------------------------------------------
  {
    beatId: "ui-chapter3-title",
    kind: "ui",
    chapter: "chapter3",
    show: "chapter3Title",
  },
  {
    beatId: "ui-prompt-scope-beekeeper",
    kind: "ui",
    chapter: "chapter3",
    show: "promptScopeBeekeeper",
  },
  {
    beatId: "stage-chapter3-agent-builder",
    kind: "stage",
    chapter: "chapter3",
    op: { kind: "checkpoint" },
  },
  // "But remember the translator guessing which love I meant? The Beekeeper guesses too. So how do
  // I check the translation, when I can't read the language?" — the flowchart is the answer, and
  // it renders the program the agent actually wrote, never a canned one.
  {
    beatId: "ui-modal-builder-flowchart-show",
    kind: "ui",
    chapter: "chapter3",
    show: "builderFlowchartModal",
  },
  {
    beatId: "ui-modal-builder-flowchart-dismiss",
    kind: "ui",
    chapter: "chapter3",
    show: null,
  },
  {
    beatId: "stage-chapter3-full-hive",
    kind: "stage",
    chapter: "chapter3",
    op: { kind: "configure", stage: CHAPTER3_FULL_HIVE_STAGE },
  },
  {
    beatId: "stage-chapter3-full-hive-runs-on",
    kind: "stage",
    chapter: "chapter3",
    op: { kind: "fastForward", ticks: 4500 },
  },

  // --- The game / two-player Live Match --------------------------------------------------------
  {
    beatId: "slide-pvp-introduction",
    kind: "slide",
    slide: "slide-pvp-introduction",
  },
  { beatId: "nav-pvp-setup", kind: "nav", target: "setup" },
  { beatId: "nav-pvp-waiting", kind: "nav", target: "setup" },
  { beatId: "nav-pvp-player-preview", kind: "nav", target: "liveMatch" },

  // --- Live play, then the hand-off ------------------------------------------------------------
  // **Two cues removed here on 2026-08-13, and the see/say/run slide moved to the tail.**
  //
  // `nav-pvp-result` was a third cue on a state the room is already looking at: the projector renders
  // the named result the moment the match reaches `finished` (`DemoLiveMatchChapters`), so by the
  // time the presenter reached the "result" cue the win had been on screen since it happened.
  //
  // `nav-rounds-handoff` is gone because its content — the address and its QR — is now the see/say/run
  // slide itself (`SeeSayRunSlide`), which is where it belongs: the framing line and the invitation
  // were one beat said twice, and the slide is the surface with the deck's own style on it. Which is
  // also why the slide sits *here* rather than back before live play — it is the hand-off now.
  { beatId: "nav-pvp-live", kind: "nav", target: "liveMatch" },
  { beatId: "nav-pvp-prompt-feed", kind: "nav", target: "liveMatch" },
  { beatId: "slide-see-say-run", kind: "slide", slide: "slide-see-say-run" },

  // --- Wrapping up -------------------------------------------------------------------------------
  { beatId: "slide-closing", kind: "slide", slide: "slide-closing" },
  // The payoff lands one beat at a time (`SUNSET_MAX_REVEAL` 2): Waggle under B++, then Octopus
  // under Sunset, so the presenter can name each as it arrives instead of reading a finished graphic.
  { beatId: "slide-sunset", kind: "slide", slide: "slide-sunset" },
  { beatId: "reveal-sunset-waggle", kind: "reveal" },
  { beatId: "reveal-sunset-octopus", kind: "reveal" },
  { beatId: "slide-starter-pack", kind: "slide", slide: "slide-starter-pack" },
  // **Last, so the deck ends on the Festival mark.** The spec's Track A table says the
  // finisher "follows the closing slide", which read literally would put it before Sunset and the
  // starter pack — but the closing sequence belongs at the actual tail: “Express your intent.”,
  // “Make your impact.”, then the Festival mark. A finisher with two more slides after it
  // after it is not an ending. The story wins; the table's phrase means the closing *section*.
  // Four states, three clicks (`FINISHER_MAX_REVEAL` 3): the empty circle the presenter speaks into,
  // then a line each, then the Festival mark over both.
  { beatId: "slide-finisher", kind: "slide", slide: "slide-finisher" },
  { beatId: "reveal-finisher-intent", kind: "reveal" },
  { beatId: "reveal-finisher-impact", kind: "reveal" },
  { beatId: "reveal-finisher-logo", kind: "reveal" },
];

/** The index of the given chapter's first cue — chapter 1's `configure`, or, for chapters 2 and 3
 * (which have none of their own), their first `ui` cue. Throws on an unknown chapter (there are
 * exactly three; a caller passing anything else is a programming error, not a presenter
 * mistake). */
export function chapterFirstIndex(
  cueList: readonly Cue[],
  chapter: ChapterId,
): number {
  const index = cueList.findIndex((cue) => cue.chapter === chapter);
  if (index === -1) {
    throw new Error(`no cue in this list belongs to chapter "${chapter}"`);
  }
  return index;
}

/** Scans backward from `cursor` for the nearest `stage` cue whose op is `configure` — the world
 * that cursor is actually standing on. Chapters 2 and 3 have none of their own, so a cursor inside
 * either resolves to chapter 1's, which is exactly the one-continuous-world relationship
 * `chapters.ts` documents; only the full-hive beat introduces a second. `undefined` before any
 * configure has been reached (the opening slides). */
export function configureAnchorIndex(
  cueList: readonly Cue[],
  cursor: number,
): number | undefined {
  for (let i = Math.min(cursor, cueList.length - 1); i >= 0; i--) {
    const cue = cueList[i];
    if (
      cue !== undefined &&
      cue.kind === "stage" &&
      cue.op.kind === "configure"
    ) {
      return i;
    }
  }
  return undefined;
}
